import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Timeline,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBolt,
  IconBug,
  IconCheck,
  IconCloudLock,
  IconCode,
  IconDoorEnter,
  IconId,
  IconKey,
  IconLock,
  IconScale,
  IconShieldLock,
  IconSparkles,
  IconUserCheck,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { signupHref } from "../lib/hosts.ts";
import { useSession } from "../session/SessionProvider.tsx";
import classes from "./Landing.module.css";

export function Security() {
  const { signedIn } = useSession();

  const primaryCta = (
    <Button component="a" href={signupHref()} size="md" rightSection={<IconArrowRight size={18} />}>
      {signedIn ? "Create an app" : "Create your account"}
    </Button>
  );

  return (
    <Box className={classes.page}>
      {/* ── Hero ───────────────────────────────────────────── */}
      <Box className={classes.hero}>
        <div className={classes.heroGlow} />
        <Stack gap="lg" className={classes.heroContent} maw={720}>
          <Badge variant="light" size="lg" leftSection={<IconShieldLock size={13} />}>
            How it works
          </Badge>
          <Title order={1} fz={{ base: 34, sm: 50 }} lh={1.07} fw={800}>
            Your app's security is{" "}
            <Text span inherit className={classes.accent}>
              handled by AWS
            </Text>{" "}
            — not by code you hope is right.
          </Title>
          <Text c="dimmed" fz="lg">
            On a normal app, your server decides who's allowed to see what — and one slip in that
            code leaks everyone's data. s3p moves that decision out of your code and into the cloud
            itself. AWS becomes the bouncer at every door. Here's the whole idea, in plain English.
          </Text>
          <Group mt="xs">{primaryCta}</Group>
        </Stack>
      </Box>

      {/* ── The big idea: an analogy ───────────────────────── */}
      <Section
        eyebrow="The whole idea"
        title="Think of it as a members-only building."
        lead="Every piece of the stack has a boring real-world job. Once you see the building, the AWS names stop mattering."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <AnalogyCard
            icon={<IconId size={20} />}
            badge="Cognito"
            title="The front desk checks your ID"
          >
            You sign in once. The front desk confirms who you are and notes which clubs you belong
            to — “member,” “admin,” “owner.”
          </AnalogyCard>
          <AnalogyCard
            icon={<IconKey size={20} />}
            badge="STS credentials"
            title="You get a keycard, cut just for you"
          >
            Instead of a master key, you're handed a temporary keycard that opens exactly your rooms
            — and quietly expires on its own. No password lives in your app.
          </AnalogyCard>
          <AnalogyCard
            icon={<IconDoorEnter size={20} />}
            badge="IAM"
            title="Every door reads the keycard"
          >
            The lock is in the wall, not in your app. Each door opens only to the rooms your keycard
            allows. Wave it at someone else's door and it simply won't open.
          </AnalogyCard>
          <AnalogyCard
            icon={<IconCloudLock size={20} />}
            badge="S3"
            title="The rooms are your data"
          >
            Your files, records, and uploads live in storage rooms. Reading and writing them is just
            opening a door — checked, every single time, by the lock in the wall.
          </AnalogyCard>
        </SimpleGrid>
      </Section>

      {/* ── What this means for you ────────────────────────── */}
      <Section
        eyebrow="Why you'll care"
        title="What that buys you."
        lead="Moving the rules out of your code and into AWS isn't a technicality — it changes what can go wrong, and how much you have to build."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <BenefitCard icon={<IconBug size={20} />} title="A bug can't become a breach">
            Even if your code is wrong, AWS still refuses to hand one user another user's data. The
            rule lives in the cloud — your app physically can't override it.
          </BenefitCard>
          <BenefitCard icon={<IconCode size={20} />} title="No auth code to babysit">
            No sessions to manage, no permission middleware, no “is this row mine?” check
            copy-pasted across twelve endpoints. You declare the rules once; AWS enforces them
            everywhere.
          </BenefitCard>
          <BenefitCard icon={<IconLock size={20} />} title="No secrets in the browser">
            Users never hold a long-lived AWS key. Just a short-lived, personalized keycard that
            expires by itself — nothing valuable to steal from a tab.
          </BenefitCard>
          <BenefitCard icon={<IconBolt size={20} />} title="Security that scales itself">
            Every request is checked by AWS at the door. There's no auth server to overload and
            nothing for you to scale — that's S3 and IAM's problem, not yours.
          </BenefitCard>
        </SimpleGrid>
      </Section>

      {/* ── What happens when you sign in ──────────────────── */}
      <Section
        eyebrow="Slightly under the hood"
        title="What happens the moment you click “sign in.”"
        lead="Four steps, all automatic. You never wire any of this up — the SDK does it — but here's the real sequence."
      >
        <Card withBorder radius="lg" padding="xl" className={classes.lift}>
          <Timeline active={4} bulletSize={26} lineWidth={2}>
            <Timeline.Item bullet={<IconId size={13} />} title="The front desk checks your ID">
              <Text c="dimmed" size="sm">
                You sign in against the project's Cognito hosted login page. AWS notes which groups
                you're in (it travels in a token as <code>cognito:groups</code>).
              </Text>
            </Timeline.Item>
            <Timeline.Item
              bullet={<IconUserCheck size={13} />}
              title="Your badge becomes a keycard"
            >
              <Text c="dimmed" size="sm">
                The Identity Pool swaps your “I'm in these groups” token for credentials cut to the
                matching access level — temporary, and scoped before they ever reach you.
              </Text>
            </Timeline.Item>
            <Timeline.Item
              bullet={<IconKey size={13} />}
              title="The keycard is temporary by design"
            >
              <Text c="dimmed" size="sm">
                These are short-lived STS credentials, not a permanent AWS key. They expire on their
                own, so there's no long-lived secret sitting in the browser.
              </Text>
            </Timeline.Item>
            <Timeline.Item
              bullet={<IconShieldLock size={13} />}
              title="Every door checks the keycard"
            >
              <Text c="dimmed" size="sm">
                On each request, AWS substitutes your own ID into the rule (
                <code>{"${jwt:sub}"}</code>) and opens only your paths. Ask for someone else's and
                S3 returns a flat “no.”
              </Text>
            </Timeline.Item>
          </Timeline>
        </Card>
      </Section>

      {/* ── The three locks ────────────────────────────────── */}
      <Section
        eyebrow="Defense in depth"
        title="Three locks — and AWS holds the keys."
        lead="Most of the work leans on the first lock, because AWS enforces it and nothing in your app can talk its way around it."
      >
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          <LockCard
            n="1"
            icon={<IconLock size={20} />}
            title="The door only opens to your stuff"
            tech="IAM policy variables — ${jwt:sub} substituted per request; S3 rejects the rest."
          >
            Each person can only read and write their own paths. This is the strongest lock: it's
            enforced by AWS itself, not by your code.
          </LockCard>
          <LockCard
            n="2"
            icon={<IconUserCheck size={20} />}
            title="Your badge decides your access"
            tech="Identity Pool maps cognito:groups → the matching IAM role."
          >
            What you're allowed to do depends on the groups you're in — member, admin, owner. Change
            someone's group and their access changes with it.
          </LockCard>
          <LockCard
            n="3"
            icon={<IconScale size={20} />}
            title="Judgment calls, handled in the open"
            tech="A server function reads the requester's groups and approves or refuses."
          >
            A few decisions need real judgment — approvals, time windows, quorum. Those run as a
            small, readable check rather than a hidden rule.
          </LockCard>
        </SimpleGrid>
      </Section>

      {/* ── Where it bends ─────────────────────────────────── */}
      <Section
        eyebrow="No magic"
        title="Where it bends — we won't hide it."
        lead="It's a sharp tool, not a silver bullet. Pick it with eyes open."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <EdgeCard title="No bank-grade transactions">
            S3 has no all-or-nothing updates across many records. Perfect for documents and feeds;
            reach for a real database when money has to balance to the cent.
          </EdgeCard>
          <EdgeCard title="You poll, not push">
            No always-on live sockets yet. Fine for most apps; true chat-grade liveness needs more.
          </EdgeCard>
          <EdgeCard title="Filenames are visible">
            AWS hides the contents of your files, but their names and sizes can be listed by anyone
            with read access. Encrypt the contents when even that matters.
          </EdgeCard>
          <EdgeCard title="It's not a drop-in Firebase">
            Same superpowers, a different set of trade-offs. If you need exactly Firebase, use
            Firebase.
          </EdgeCard>
        </SimpleGrid>
        <Text size="sm" mt="xs" c="dimmed">
          Want the head-to-head? See the <Anchor href="/COMPARISON.md">full comparison</Anchor>, or
          the precise vocabulary in <Anchor href="/DEFINITIONS.md">DEFINITIONS.md</Anchor>.
        </Text>
      </Section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <Box className={classes.ctaBand} p={{ base: "xl", sm: 48 }} my={64}>
        <Stack align="center" ta="center" gap="md">
          <ThemeIcon size={52} radius="xl" variant="light">
            <IconSparkles size={28} />
          </ThemeIcon>
          <Title order={2} fz={{ base: 26, sm: 34 }} maw={620}>
            Let AWS be the bouncer. You just ship.
          </Title>
          <Text c="dimmed" maw={520}>
            Name your app, and seconds later you're writing data — with per-user security
            already enforced.
          </Text>
          <Group mt="sm">
            {primaryCta}
            <Button component={Link} to="/" size="md" variant="default">
              Back to overview
            </Button>
          </Group>
        </Stack>
      </Box>
    </Box>
  );
}

/* ── Section scaffold (matches Landing) ───────────────────── */

function Section({
  eyebrow,
  title,
  lead,
  children,
}: {
  eyebrow: string;
  title: React.ReactNode;
  lead?: string;
  children?: React.ReactNode;
}) {
  return (
    <Stack gap="xl" py={56}>
      <Stack gap="sm" maw={760}>
        <Text tt="uppercase" fz="xs" fw={700} lts={2} className={classes.accent}>
          {eyebrow}
        </Text>
        <Title order={2} fz={{ base: 26, sm: 36 }} lh={1.12}>
          {title}
        </Title>
        {lead && (
          <Text c="dimmed" fz="lg">
            {lead}
          </Text>
        )}
      </Stack>
      {children}
    </Stack>
  );
}

function AnalogyCard({
  icon,
  badge,
  title,
  children,
}: {
  icon: React.ReactNode;
  badge: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card withBorder radius="lg" padding="xl" h="100%" className={classes.lift}>
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <ThemeIcon variant="light" size={44} radius="md">
          {icon}
        </ThemeIcon>
        <Badge variant="default" size="sm">
          {badge}
        </Badge>
      </Group>
      <Text fw={700} mb={4}>
        {title}
      </Text>
      <Text c="dimmed" size="sm">
        {children}
      </Text>
    </Card>
  );
}

function BenefitCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card withBorder radius="lg" padding="xl" h="100%" className={classes.lift}>
      <Group gap="sm" mb="sm" wrap="nowrap">
        <ThemeIcon variant="light" size="lg" radius="md">
          {icon}
        </ThemeIcon>
        <Text fw={700}>{title}</Text>
      </Group>
      <Text c="dimmed" size="sm">
        {children}
      </Text>
    </Card>
  );
}

function LockCard({
  n,
  icon,
  title,
  tech,
  children,
}: {
  n: string;
  icon: React.ReactNode;
  title: string;
  tech: string;
  children: React.ReactNode;
}) {
  return (
    <Card withBorder radius="lg" padding="xl" h="100%" className={classes.lift}>
      <Group gap="sm" mb="sm" wrap="nowrap">
        <ThemeIcon variant="light" size="lg" radius="md">
          {icon}
        </ThemeIcon>
        <Text fw={700} c="dimmed" fz="sm">
          Lock {n}
        </Text>
      </Group>
      <Text fw={700} mb={4}>
        {title}
      </Text>
      <Text c="dimmed" size="sm" mb="sm">
        {children}
      </Text>
      <Group gap={6} wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color="gray" size="sm" radius="xl" mt={1}>
          <IconCheck size={11} />
        </ThemeIcon>
        <Text size="xs" c="dimmed" style={{ fontFamily: "var(--mantine-font-family-monospace)" }}>
          {tech}
        </Text>
      </Group>
    </Card>
  );
}

function EdgeCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card withBorder radius="lg" padding="lg" className={classes.lift}>
      <Group gap="xs" mb={6}>
        <ThemeIcon variant="light" color="yellow" size="sm" radius="xl">
          <IconLock size={12} />
        </ThemeIcon>
        <Text fw={600}>{title}</Text>
      </Group>
      <Text c="dimmed" size="sm">
        {children}
      </Text>
    </Card>
  );
}
